const HORIZONTAL_STEP = 340;
const VERTICAL_STEP = 142;

export const REPRESENTATIVE_MAP_SIZES = Object.freeze([10, 100, 1000]);
export const BROWSER_LARGE_MAP_SIZES = Object.freeze([250, 1000, 5000]);
export const PRODUCTION_LARGE_MAP_NODE_COUNT = 1000;

const PRODUCTION_BRANCH_COUNT = 24;
const PRODUCTION_CHILDREN_PER_BRANCH_NODE = 4;

function productionNodeContent(index) {
  if (index > 0 && index % 41 === 0) {
    return {
      text: `research-note-${index}.md`,
      attachment: {
        assetId: `fixture-markdown-${index}`,
        kind: 'markdown',
        filename: `research-note-${index}.md`,
        contentType: 'text/markdown',
        sizeBytes: 2048 + index,
      },
    };
  }
  if (index > 0 && index % 53 === 0) {
    return {
      text: `concept-${index}.webp`,
      attachment: {
        assetId: `fixture-image-${index}`,
        kind: 'image',
        filename: `concept-${index}.webp`,
        contentType: 'image/webp',
        sizeBytes: 16384 + index,
      },
    };
  }
  if (index > 0 && index % 17 === 0) {
    return {
      text: `Reference card ${index}`,
      linkUrl: `https://example.com/research/${index}`,
      linkMeta: {
        title: `Customer research reference ${index}`,
        description: 'Cached fixture metadata; no external request is required.',
        siteName: 'Example research',
      },
    };
  }
  if (index > 0 && index % 11 === 0) {
    return {
      text: `## Release workstream ${index}\n\nReview the customer evidence, decide an owner, and record the next action before the beta checkpoint.`,
    };
  }
  return { text: index === 0 ? 'Mindmappi production planning' : `Production idea ${index}` };
}

/**
 * A deterministic feature-dense browser fixture. Its topology deliberately
 * combines wide sibling fans with deeper branches, while unique world-space
 * rows keep the starting geometry valid and make culling predictable.
 */
export function generateProductionLargeMapFixture(
  nodeCount = PRODUCTION_LARGE_MAP_NODE_COUNT
) {
  if (nodeCount !== PRODUCTION_LARGE_MAP_NODE_COUNT) {
    throw new TypeError(`production large-map fixture requires ${PRODUCTION_LARGE_MAP_NODE_COUNT} nodes`);
  }

  const nodes = [];
  const relationships = [];
  const branchNodeIds = Array.from(
    { length: PRODUCTION_BRANCH_COUNT },
    (_, branchIndex) => `production-node-${branchIndex + 1}`
  );
  const levelSlots = new Map();
  const depths = [0];
  const topBranches = [null];

  for (let index = 0; index < nodeCount; index += 1) {
    const id = `production-node-${index}`;
    let parentIndex = null;
    if (index > 0) {
      if (index <= PRODUCTION_BRANCH_COUNT) {
        parentIndex = 0;
      } else {
        parentIndex = 1 + Math.floor(
          (index - PRODUCTION_BRANCH_COUNT - 1) / PRODUCTION_CHILDREN_PER_BRANCH_NODE
        );
      }
    }
    const depth = parentIndex === null ? 0 : depths[parentIndex] + 1;
    depths[index] = depth;
    const topBranch = index === 0
      ? null
      : parentIndex === 0
        ? index - 1
        : topBranches[parentIndex];
    topBranches[index] = topBranch;
    const side = index === 0 || topBranch % 2 === 0 ? 'right' : 'left';
    const levelKey = `${side}:${depth}`;
    const slot = index === 0 ? 0 : (levelSlots.get(levelKey) || 0);
    if (index > 0) levelSlots.set(levelKey, slot + 1);
    const y = index === 0 ? 0 : slot * VERTICAL_STEP;
    const x = index === 0
      ? 0
      : side === 'right'
        ? depth * HORIZONTAL_STEP
        : -(depth * HORIZONTAL_STEP + 260);
    const content = productionNodeContent(index);

    nodes.push({
      id,
      ...content,
      position: { x, y },
      layoutSize: { width: 260, height: 110 },
      layout: { side, order: slot, pinned: index === 0 },
      isRoot: index === 0,
      isTask: index > 0 && index % 7 === 0,
      taskDone: index > 0 && index % 14 === 0,
    });

    if (parentIndex !== null) {
      relationships.push({
        parentId: `production-node-${parentIndex}`,
        childId: id,
      });
    }
  }

  return {
    name: '1000-idea-production-shaped-map',
    root: 'production-node-0',
    selectedId: 'production-node-0',
    nodes,
    relationships,
    profile: {
      collapsedBranchIds: [branchNodeIds[0], branchNodeIds[1]],
      focusBranchId: branchNodeIds[2],
      offscreenSearchId: 'production-node-900',
    },
  };
}

export function generateLargeMapFixture(nodeCount, options = {}) {
  if (!Number.isInteger(nodeCount) || nodeCount < 1) {
    throw new TypeError('nodeCount must be a positive integer');
  }
  const branchingFactor = Number.isInteger(options.branchingFactor) && options.branchingFactor > 0
    ? options.branchingFactor
    : 4;
  const nodes = [];
  const relationships = [];
  const depths = [0];
  const levelSlots = new Map([[0, 0]]);

  for (let index = 0; index < nodeCount; index += 1) {
    const id = `fixture-node-${index}`;
    const parentIndex = index === 0 ? null : Math.floor((index - 1) / branchingFactor);
    const depth = parentIndex === null ? 0 : depths[parentIndex] + 1;
    depths[index] = depth;
    const slot = levelSlots.get(depth) || 0;
    levelSlots.set(depth, slot + 1);
    const isLink = index > 0 && index % 17 === 0;

    nodes.push({
      id,
      text: index % 11 === 0
        ? `## Release section ${index}\nDetailed fixture content for navigation and search.`
        : `Fixture node ${index}`,
      position: { x: depth * HORIZONTAL_STEP, y: slot * VERTICAL_STEP },
      layoutSize: { width: 260, height: 110 },
      isRoot: index === 0,
      isTask: index > 0 && index % 7 === 0,
      taskDone: index > 0 && index % 14 === 0,
      linkUrl: isLink ? `https://example.com/reference/${index}` : null,
      linkMeta: isLink ? { title: `Reference ${index}`, description: 'Fixture link metadata' } : null,
    });

    if (parentIndex !== null) {
      relationships.push({
        parentId: `fixture-node-${parentIndex}`,
        childId: id,
      });
    }
  }

  return {
    name: `${nodeCount}-node-balanced-map`,
    root: 'fixture-node-0',
    selectedId: 'fixture-node-0',
    nodes,
    relationships,
  };
}
